import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PlatformLogo } from "@/components/PlatformLogo";
import ManifestBuilder from "@/components/ManifestBuilder";
import { Plus, Trash, PencilSimple, DownloadSimple, Cube, Lightning, Question } from "@phosphor-icons/react";

const TEMPLATE = {
  platform: "",
  label: "",
  category: "custom",
  logo_url: "",
  base_url: "",
  auth: { type: "bearer", value_field: "access_token" },
  credential_fields: [{ name: "access_token", label: "Access Token", secret: true, required: true }],
  settings_fields: [],
  test: { path: "", method: "GET" },
  objects: [
    {
      name: "", label: "",
      list: { path: "", method: "GET", records_path: "data" },
      create: { path: "", method: "POST" },
      id_field: "id",
      pagination: { type: "offset", limit_param: "limit", offset_param: "offset" },
      discovery: { type: "sample" },
    },
  ],
  trial: { enabled: false },
};

const clone = (o) => JSON.parse(JSON.stringify(o));

export default function ConnectorStudio() {
  const [manifests, setManifests] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState("form");
  const [draft, setDraft] = useState(TEMPLATE);
  const [jsonText, setJsonText] = useState("");
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  const load = () => api.get("/connector-studio/manifests").then((r) => setManifests(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null); setMode("form");
    setDraft(clone(TEMPLATE));
    setJsonText(JSON.stringify(TEMPLATE, null, 2));
    setOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m.platform); setMode("form");
    const { id, _id, created_at, updated_at, ...cleanRest } = m;
    setDraft(clone(cleanRest));
    setJsonText(JSON.stringify(cleanRest, null, 2));
    setOpen(true);
  };

  const switchMode = (target) => {
    if (target === mode) return;
    if (target === "json") {
      setJsonText(JSON.stringify(draft, null, 2));
    } else {
      try {
        setDraft(JSON.parse(jsonText));
      } catch (e) { toast.error("Fix JSON before switching: " + e.message); return; }
    }
    setMode(target);
  };

  const save = async () => {
    let payload = draft;
    if (mode === "json") {
      try { payload = JSON.parse(jsonText); }
      catch (e) { toast.error("Invalid JSON: " + e.message); return; }
    }
    if (!payload.platform) { toast.error("Platform key is required"); return; }
    setSaving(true);
    try {
      await api.post("/connector-studio/manifests", payload);
      toast.success(`Connector "${payload.label || payload.platform}" saved`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (platform) => {
    if (!window.confirm(`Delete connector "${platform}"? Existing connections using it will stop working.`)) return;
    await api.delete(`/connector-studio/manifests/${platform}`);
    toast.success("Connector deleted");
    load();
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/connector-studio/import-openapi", { url: importUrl });
      setImportOpen(false); setImportUrl("");
      setEditing(null); setMode("form");
      setDraft(data); setJsonText(JSON.stringify(data, null, 2));
      setOpen(true);
      toast.success("Draft generated from OpenAPI spec — review & save");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  return (
    <Layout>
      <PageHeader
        title="Connector Studio"
        subtitle="Build new app connectors from config — no code. Scales to hundreds of systems."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/connector-guide">
              <Button data-testid="setup-guide-btn" variant="outline" className="rounded-xl gap-2">
                <Question size={16} /> How to set up a connector
              </Button>
            </Link>
            <Button data-testid="import-openapi-btn" variant="outline" onClick={() => setImportOpen(true)}
              className="rounded-xl gap-2">
              <DownloadSimple size={16} /> Import from OpenAPI
            </Button>
            <Button data-testid="new-connector-btn" onClick={openNew}
              className="rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200 gap-2">
              <Plus size={16} /> New Connector
            </Button>
          </div>
        }
      />
      <div className="p-8 space-y-6">
        <div className="bg-[#C2410C]/5 border border-[#C2410C]/20 rounded-xl p-4 text-sm text-stone-600 leading-relaxed max-w-3xl">
          <div className="flex items-center gap-2 font-medium text-stone-900 mb-1">
            <Lightning size={16} weight="fill" className="text-[#C2410C]" /> How this scales
          </div>
          Teach the platform about an app <strong>once</strong> here — then anyone connects it from the Connections
          page like Salesforce or HubSpot. Each connector is a declarative manifest run by one generic REST engine;
          the migration engine never changes. Logos resolve automatically from the platform key.
        </div>

        {manifests.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-300 rounded-xl py-20 text-center">
            <Cube size={40} className="mx-auto text-stone-300" />
            <p className="text-stone-500 mt-4 text-sm">No custom connectors yet. Create one or import an OpenAPI spec.</p>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl shadow-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                  <th className="px-6 py-3 font-semibold">Connector</th>
                  <th className="px-6 py-3 font-semibold">Key</th>
                  <th className="px-6 py-3 font-semibold">Category</th>
                  <th className="px-6 py-3 font-semibold">Auth</th>
                  <th className="px-6 py-3 font-semibold">Objects</th>
                  <th className="px-6 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {manifests.map((m) => (
                  <tr key={m.platform} data-testid={`manifest-row-${m.platform}`}
                    className="border-b border-stone-100 hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <PlatformLogo platform={m.platform} size={22} logoUrl={m.logo_url} baseUrl={m.base_url} />
                        <span className="font-medium text-stone-900">{m.label}</span>
                        {m.trial?.enabled && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-xl text-[10px]">Trial</Badge>}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-stone-500">{m.platform}</td>
                    <td className="px-6 py-4 text-xs capitalize">{m.category || "—"}</td>
                    <td className="px-6 py-4 font-mono text-xs">{m.auth?.type || "none"}</td>
                    <td className="px-6 py-4 text-xs">{(m.objects || []).length}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button data-testid={`edit-manifest-${m.platform}`} size="sm" variant="outline"
                          onClick={() => openEdit(m)} className="rounded-xl gap-1.5 text-xs">
                          <PencilSimple size={14} /> Edit
                        </Button>
                        <Button data-testid={`delete-manifest-${m.platform}`} size="sm" variant="ghost"
                          onClick={() => del(m.platform)} className="rounded-xl text-rose-600 hover:bg-rose-50">
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit manifest */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl max-w-3xl" data-testid="manifest-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <DialogTitle className="font-head">{editing ? `Edit Connector: ${editing}` : "New Connector"}</DialogTitle>
              <div className="flex rounded-xl border border-stone-200 overflow-hidden text-xs">
                <button data-testid="mode-form" onClick={() => switchMode("form")}
                  className={`px-3 py-1.5 ${mode === "form" ? "bg-[#C2410C] text-white" : "bg-white text-stone-600"}`}>Form</button>
                <button data-testid="mode-json" onClick={() => switchMode("json")}
                  className={`px-3 py-1.5 ${mode === "json" ? "bg-[#C2410C] text-white" : "bg-white text-stone-600"}`}>JSON (advanced)</button>
              </div>
            </div>
          </DialogHeader>

          <div className="py-2">
            {mode === "form" ? (
              <ManifestBuilder value={draft} onChange={setDraft} />
            ) : (
              <>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Manifest (JSON)</Label>
                <textarea data-testid="manifest-json"
                  value={jsonText} onChange={(e) => setJsonText(e.target.value)} spellCheck={false}
                  className="mt-1 w-full h-[420px] font-mono text-xs bg-stone-950 text-emerald-100 rounded-xl p-4 border border-stone-800 focus:outline-none focus:ring-1 focus:ring-[#C2410C]" />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-manifest-btn" onClick={save} disabled={saving}
              className="rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200">
              {saving ? "Saving…" : "Save Connector"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from OpenAPI */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-xl" data-testid="import-dialog">
          <DialogHeader><DialogTitle className="font-head">Import from OpenAPI / Swagger</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-stone-500">
              Paste a public OpenAPI/Swagger JSON URL. We'll draft a connector manifest you can review and edit.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">OpenAPI Spec URL</Label>
              <Input data-testid="import-url" value={importUrl} onChange={(e) => setImportUrl(e.target.value)}
                placeholder="https://petstore3.swagger.io/api/v3/openapi.json" className="mt-1 rounded-xl font-mono text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button data-testid="run-import-btn" onClick={runImport} disabled={!importUrl || importing}
              className="rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200">
              {importing ? "Importing…" : "Generate Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
